import { Controller } from "@hotwired/stimulus";
import {
  MIN_REMOTE_QUERY_LENGTH,
  isCommandSearchShortcut,
  normalizeTransientNavigationState,
  platformModifierLabel,
  rankNavigationResults,
} from "../lib/partner_navigation_search.mjs";

export default class extends Controller {
  static targets = ["overlay", "input", "results", "status", "filter", "menu", "menuButton", "modifier"];
  static values = {
    pages: Array,
    searchUrl: String,
  };

  connect() {
    this.records = [];
    this.visibleResults = [];
    this.activeFilter = "all";
    this.selectedIndex = -1;
    this.searchOpen = false;
    this.loading = false;
    this.searchTimer = null;
    this.abortController = null;
    this.previouslyFocusedElement = null;

    const platform = navigator.userAgentData?.platform || navigator.platform || "";
    const modifier = platformModifierLabel(platform);
    this.modifierTargets.forEach((target) => { target.textContent = modifier; });
  }

  disconnect() {
    this.prepareForCache();
  }

  handleGlobalKeydown(event) {
    if (isCommandSearchShortcut(event)) {
      event.preventDefault();
      this.searchOpen ? this.closeSearch() : this.openSearch();
      return;
    }

    if (event.key !== "Escape") return;

    if (this.searchOpen) {
      event.preventDefault();
      this.closeSearch();
    } else if (this.menuIsOpen()) {
      event.preventDefault();
      this.closeMenu();
    }
  }

  openSearch() {
    this.closeMenu();
    this.previouslyFocusedElement = document.activeElement;
    this.searchOpen = true;
    this.overlayTarget.hidden = false;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.inputTarget.value = "";
    this.records = [];
    this.loading = false;
    this.setFilter("all");
    this.renderResults();
    window.requestAnimationFrame(() => this.inputTarget.focus());
  }

  closeSearch() {
    this.searchOpen = false;
    this.overlayTarget.hidden = true;
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.restoreBodyScroll();
    if (this.previouslyFocusedElement?.isConnected) this.previouslyFocusedElement.focus();
    this.previouslyFocusedElement = null;
  }

  prepareForCache() {
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    normalizeTransientNavigationState({
      overlay: this.hasOverlayTarget ? this.overlayTarget : null,
      menu: this.hasMenuTarget ? this.menuTarget : null,
      menuButtons: this.menuButtonTargets,
      body: document.body,
      previousBodyOverflow: this.previousBodyOverflow,
    });
    this.searchOpen = false;
    this.loading = false;
    this.searchTimer = null;
    this.abortController = null;
    this.previousBodyOverflow = undefined;
    this.previouslyFocusedElement = null;
  }

  closeFromBackdrop(event) {
    if (event.target === this.overlayTarget) this.closeSearch();
  }

  toggleMenu(event) {
    event.stopPropagation();
    const shouldOpen = this.menuTarget.classList.contains("hidden");
    shouldOpen ? this.openMenu() : this.closeMenu();
  }

  openMenu() {
    this.menuTarget.classList.remove("hidden");
    this.menuButtonTargets.forEach((target) => target.setAttribute("aria-expanded", "true"));
  }

  closeMenu() {
    this.menuTarget.classList.add("hidden");
    this.menuButtonTargets.forEach((target) => target.setAttribute("aria-expanded", "false"));
  }

  closeMenuFromOutside(event) {
    if (!this.menuIsOpen() && !this.searchOpen) return;
    if (this.searchOpen || this.menuTarget.contains(event.target)) return;
    if (this.menuButtonTargets.some((target) => target.contains(event.target))) return;

    this.closeMenu();
  }

  search() {
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.records = [];
    this.loading = false;
    this.selectedIndex = -1;
    this.renderResults();

    const query = this.query();
    if (query.length < MIN_REMOTE_QUERY_LENGTH) return;

    this.loading = true;
    this.renderResults();
    this.searchTimer = window.setTimeout(() => this.fetchRemoteResults(query), 150);
  }

  changeFilter(event) {
    this.setFilter(event.currentTarget.dataset.filter);
    this.renderResults();
    this.inputTarget.focus();
  }

  handleInputKeydown(event) {
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;

    event.preventDefault();
    if (!this.visibleResults.length) return;

    if (event.key === "ArrowDown") {
      this.selectIndex((this.selectedIndex + 1) % this.visibleResults.length);
    } else if (event.key === "ArrowUp") {
      const previous = this.selectedIndex <= 0 ? this.visibleResults.length - 1 : this.selectedIndex - 1;
      this.selectIndex(previous);
    } else {
      this.activateResult(this.visibleResults[Math.max(this.selectedIndex, 0)], event.metaKey || event.ctrlKey);
    }
  }

  selectFromPointer(event) {
    const result = event.target.closest("[data-result-index]");
    if (!result) return;

    this.selectIndex(Number(result.dataset.resultIndex), false);
  }

  activateFromPointer(event) {
    const result = event.target.closest("[data-result-index]");
    if (!result) return;

    this.activateResult(this.visibleResults[Number(result.dataset.resultIndex)], event.metaKey || event.ctrlKey);
  }

  async fetchRemoteResults(query) {
    this.abortController = new AbortController();
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Navigation search failed with ${response.status}`);

      const payload = await response.json();
      if (query !== this.query()) return;

      this.records = [...(payload.claims || []), ...(payload.members || [])];
      this.loading = false;
      this.renderResults();
    } catch (error) {
      if (error.name === "AbortError") return;

      this.loading = false;
      this.statusTarget.textContent = "Record search is unavailable. Page shortcuts still work.";
      this.statusTarget.classList.remove("hidden");
    }
  }

  renderResults() {
    this.visibleResults = rankNavigationResults(this.pagesValue, this.records, this.query(), this.activeFilter);
    this.resultsTarget.replaceChildren();

    let lastType = null;
    this.visibleResults.forEach((result, index) => {
      if (result.type !== lastType) {
        this.resultsTarget.append(this.buildSectionLabel(result.type));
        lastType = result.type;
      }
      this.resultsTarget.append(this.buildResult(result, index));
    });

    if (this.visibleResults.length) {
      this.statusTarget.classList.add("hidden");
      this.selectIndex(0, false);
    } else {
      this.selectedIndex = -1;
      this.inputTarget.removeAttribute("aria-activedescendant");
      this.statusTarget.textContent = this.emptyMessage();
      this.statusTarget.classList.remove("hidden");
    }

    if (this.loading) {
      this.statusTarget.textContent = "Searching claims and members…";
      this.statusTarget.classList.remove("hidden");
    }
  }

  buildSectionLabel(type) {
    const label = document.createElement("div");
    label.className = "px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-support-dark-500";
    label.textContent = { page: "Pages", claim: "Claims", member: "Members" }[type] || "Results";
    return label;
  }

  buildResult(result, index) {
    const button = document.createElement("button");
    button.type = "button";
    button.id = `partner-navigation-result-${index}`;
    button.dataset.resultIndex = index;
    button.setAttribute("role", "option");
    button.setAttribute("aria-selected", "false");
    button.className = "w-full grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-transparent !bg-support-light-200 px-3 py-2.5 text-left !text-support-dark-800 hover:!bg-support-light-250 focus:outline-none";

    const icon = document.createElement("span");
    icon.className = "flex h-9 w-9 items-center justify-center rounded-lg bg-support-light-200 text-sm font-semibold text-core-purple-700";
    icon.textContent = { page: "↗", claim: "#", member: "M" }[result.type] || "•";

    const copy = document.createElement("span");
    copy.className = "min-w-0";
    const title = document.createElement("span");
    title.className = "block truncate text-sm font-semibold text-support-dark-800";
    title.textContent = result.title;
    const meta = document.createElement("span");
    meta.className = "block truncate text-xs text-support-dark-600";
    meta.textContent = result.meta || "Open page";
    copy.append(title, meta);

    const type = document.createElement("span");
    type.className = "hidden rounded-full bg-support-light-200 px-2 py-1 text-[11px] font-semibold text-support-dark-600 sm:inline";
    type.textContent = result.type.charAt(0).toUpperCase() + result.type.slice(1);

    button.append(icon, copy, type);
    return button;
  }

  selectIndex(index, shouldScroll = true) {
    this.selectedIndex = index;
    this.resultsTarget.querySelectorAll("[data-result-index]").forEach((element, elementIndex) => {
      const selected = elementIndex === index;
      element.setAttribute("aria-selected", String(selected));
      element.classList.toggle("!bg-support-light-200", !selected);
      element.classList.toggle("!bg-core-purple-200", selected);
      element.classList.toggle("border-support-light-300", selected);
      if (selected && shouldScroll) element.scrollIntoView({ block: "nearest" });
    });

    if (index >= 0) this.inputTarget.setAttribute("aria-activedescendant", `partner-navigation-result-${index}`);
  }

  activateResult(result, newTab) {
    if (!result?.url) return;

    if (newTab) {
      window.open(result.url, "_blank", "noopener,noreferrer");
      return;
    }

    this.prepareForCache();
    if (window.Turbo?.visit) {
      window.Turbo.visit(result.url);
    } else {
      window.location.assign(result.url);
    }
  }

  setFilter(filter) {
    this.activeFilter = filter || "all";
    this.filterTargets.forEach((target) => {
      const selected = target.dataset.filter === this.activeFilter;
      target.setAttribute("aria-pressed", String(selected));
      target.classList.toggle("!bg-support-light-200", selected);
      target.classList.toggle("!text-core-purple-700", selected);
      target.classList.toggle("shadow-sm", selected);
      target.classList.toggle("!bg-transparent", !selected);
      target.classList.toggle("!text-support-dark-600", !selected);
    });
  }

  emptyMessage() {
    if (!this.query()) {
      return this.activeFilter === "page" ? "No page shortcuts are available." : "Type at least two characters to search records.";
    }
    if (this.query().length < MIN_REMOTE_QUERY_LENGTH && this.activeFilter !== "page") return "Keep typing to search claims and members.";
    return "No matching pages, claims, or members.";
  }

  query() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : "";
  }

  menuIsOpen() {
    return this.hasMenuTarget && !this.menuTarget.classList.contains("hidden");
  }

  restoreBodyScroll() {
    if (this.previousBodyOverflow === undefined) return;

    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = undefined;
  }
}
